import { ARDOQ_DEFAULT_LOCALE, localeCompareBase } from '@ardoq/locale';
import { APIFieldType } from '@ardoq/api-types';
import type { Field } from 'integrations/common/streams/fields/types';
import fp from 'lodash/fp';
import {
  ActionCreatorWithoutNamespace,
  NamespacedActionCreator,
} from '@ardoq/rxbeach';

/**
 * sorting utils
 */
type ByIndexItem = {
  index: number;
};
export const byIndex = (a: ByIndexItem, b: ByIndexItem) => a.index - b.index;

type ByFieldNameItem<FieldName extends string> = {
  [K in FieldName]: string;
};
// Alphabetical ordering of entity labels (workspace names, field labels, region
// names, VPC labels, etc.) — not user-locale-sensitive, so sort by the default
// locale rather than reading the current one. Keeps this comparator pure.
export const byLocaleField =
  <FieldName extends string>(field: FieldName) =>
  (a: ByFieldNameItem<FieldName>, b: ByFieldNameItem<FieldName>) =>
    localeCompareBase(a[field], b[field], ARDOQ_DEFAULT_LOCALE);

export const byNumericValueAsc = (a: number, b: number) => a - b;
export const byNumericValueDesc = (a: number, b: number) => b - a;
/**
 * case transforming utils
 */

export const toPascalCase = (text: string): string => {
  return text.replace(/(^|-)(\w)/g, (_, __, char) => char.toUpperCase());
};

export const filterFieldsByType = fp.curry(
  (types: APIFieldType[], fields: Field[]): Field[] =>
    fields.filter(({ type }) => !type || types.includes(type as APIFieldType))
);

export const areArraysEqual = (array1: string[], array2: string[]) => {
  const sortedArray1 = fp.sortBy(fp.identity)(array1);
  const sortedArray2 = fp.sortBy(fp.identity)(array2);
  return fp.isEqual(sortedArray1, sortedArray2);
};

/**
 * Binds a namespace to a namespaced action creator, returning a regular action creator.
 *
 * This utility transforms a NamespacedActionCreator (which requires both namespace
 * and payload) into an ActionCreatorWithoutNamespace (which only requires payload)
 * by pre-binding a specific namespace.
 *
 * Use case: When you need to pass a namespaced action creator as a callback but
 * the callback signature expects a regular action creator that only takes payload.
 *
 * @example
 * ```typescript
 * // Given a namespaced action
 * const myAction = namespacedActionCreator<Payload>('[namespace] MY_ACTION');
 *
 * // Bind it to a specific namespace
 * const boundAction = bindNamespaceToAction('my-integration-id', myAction);
 *
 * // Now it can be called with just payload
 * boundAction(payload); // Equivalent to myAction('my-integration-id', payload)
 * ```
 *
 * @template Payload - The type of the action payload
 * @param namespace - The namespace string to bind to the action
 * @param namespacedAction - The namespaced action creator to bind
 * @returns An action creator that only requires payload (namespace is pre-bound)
 */
export const bindNamespaceToAction = <Payload>(
  namespace: string,
  namespacedAction: NamespacedActionCreator<Payload>
): ActionCreatorWithoutNamespace<Payload> => {
  const boundCreator = (payload: Payload) =>
    namespacedAction(namespace, payload);
  boundCreator.type = namespacedAction.type;
  boundCreator.actionCreatorSymbol = namespacedAction.actionCreatorSymbol;

  return Object.freeze(boundCreator) as ActionCreatorWithoutNamespace<Payload>;
};

export const generateMappingId = () =>
  Math.random().toString(36).substring(2, 15);
